import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/system';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { useMemo } from 'react';

import { DataValueItem } from '~components/Common/DataValueItem';
import { SystemRoles } from '~constants/enums';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { RoleItem } from '~hooks/useUsers/models';
import { getFormattedRateValue } from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store';
import { extractRateInfoFromJob } from '~utils/jobs/job-utils';
import { extractRateInfoFromOrderOrProject, userCompanyOwnsOrder } from '~utils/orders';

interface Props {
  details: Job | Order;
  sx?: SxProps<Theme>;
}

const isOrder = (details: Job | Order): details is Order => {
  return (details as Job).jobId === undefined;
};

const BillingInfo = ({ details, sx = {} }: Props) => {
  const { userStore, ordersDispatchStore } = useStores();
  const isOrderType = isOrder(details);
  const userRoles = userStore.user.userRoles;
  const userIsOnlyDriverOrForeman = userRoles?.every(
    (role: RoleItem) =>
      role.name === SystemRoles.FOREMAN || role.name === SystemRoles.DRIVER,
  );

  const order = isOrderType
    ? details
    : ordersDispatchStore.findJobAssociatedOrder(details.id);
  const isOrderOwner = order ? userCompanyOwnsOrder(userStore, order) : false;

  const rates = useMemo(() => {
    const rates: Array<{
      label: string;
      rateType: string;
      rateValue: number | null;
    }> = [];

    if (!userIsOnlyDriverOrForeman) {
      if (isOrderOwner) {
        const customerRateInfo = isOrderType
          ? extractRateInfoFromOrderOrProject(details, 'customer')
          : extractRateInfoFromJob(details, 'customer');

        if (customerRateInfo) {
          rates.push({ label: 'dispatch.job.customer_rate', ...customerRateInfo });
        }
      }

      if (!isOrderOwner) {
        const haulRateInfo = isOrderType
          ? extractRateInfoFromOrderOrProject(details, 'haul')
          : extractRateInfoFromJob(details, 'haul');

        if (haulRateInfo) {
          rates.push({ label: 'dispatch.job.haul_rate', ...haulRateInfo });
        }
      }

      const vendorRateInfo = isOrderType
        ? extractRateInfoFromOrderOrProject(details, 'pay')
        : extractRateInfoFromJob(details, 'pay');

      if (vendorRateInfo) {
        rates.push({ label: 'dispatch.job.pay_rate', ...vendorRateInfo });
      }
    }

    const driverRateInfo = isOrderType
      ? extractRateInfoFromOrderOrProject(details, 'driver')
      : extractRateInfoFromJob(details, 'driver');

    if (driverRateInfo) {
      rates.push({ label: 'dispatch.job.driver_rate', ...driverRateInfo });
    }

    return rates;
  }, [userIsOnlyDriverOrForeman, isOrderOwner, isOrderType, details]);

  return (
    <Box sx={{ ...sx }}>
      <Typography variant="group-title">
        {t('form_fields.billing_information')}
      </Typography>

      {rates.map((rate) => (
        <RateRow
          key={rate.label}
          label={t(rate.label)}
          rateType={rate.rateType as RateType}
          rateValue={rate.rateValue}
        />
      ))}
    </Box>
  );
};

interface RateRowProps {
  label: string;
  rateType: RateType;
  rateValue: number | null;
}

function RateRow({ label, rateType, rateValue }: RateRowProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const formattedValue = getFormattedRateValue(
    rateType,
    rateValue,
    currencyFormatter,
    true,
  );

  return (
    <Grid container spacing={2} sx={{ mb: 1.25 }}>
      <Grid size={{ xs: 6 }}>
        <DataValueItem label={label} value={formattedValue || '-'} />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <DataValueItem
          label={t('form_fields.rate_type')}
          value={t(`rate_types.${snakeCase(rateType)}`)}
        />
      </Grid>
    </Grid>
  );
}

export { BillingInfo };
